/**
 * The id a RUN is grouped, written and revealed by (review F-7).
 *
 * `BatchPanel` built it from `Date.now().toString(36)` alone. That id is not decoration: the
 * Queue groups a run's jobs by it, `app/bootstrap.ts` keys the run's ONE `RunWriter` — and so its
 * run folder, its claim map and its single automatic reveal — on it, and it is persisted with
 * every job record. Two runs sharing one millisecond therefore shared all of that: the second
 * run's jobs joined the first run's row, wrote through the first run's writer, and the reveal
 * fired once for both.
 *
 * A millisecond is not a long time in this app. "Create Mock-up" enqueues one job per template
 * inside a single loop, a restore replays several at once, and nothing between them awaits the
 * clock.
 *
 * THREE PARTS, each earning its place:
 *   - `Date.now()` in base 36 — kept, because a sortable, readable stamp is why these ids are
 *     legible in the Queue and in a trace at all;
 *   - a process-monotonic counter — the part that actually guarantees uniqueness WITHIN a run of
 *     the app, which is where every collision this fixes happens;
 *   - four random base-36 characters — the counter restarts at zero on every launch, so two
 *     launches racing one millisecond would otherwise both produce `…-1`.
 */

/** Monotonic within this app run. Wraps well past any plausible session, and the wrap only
 *  matters if the clock AND the random segment also repeat. */
let seq = 0;

/** Four base-36 characters, and never a security token — this is a collision guard between two
 *  app launches, not an unguessable id. */
function randomTail(): string {
  return Math.random().toString(36).slice(2, 6).padEnd(4, "0");
}

/**
 * `<prefix>-<ms in base36>-<counter in base36>-<4 random base36>`.
 *
 * Lowercase base 36 and the separator, which is the alphabet these ids have always used: they
 * reach `batchId`, the queue's grouping key, and the persisted job record.
 */
export function nextRunId(prefix: string): string {
  seq = (seq + 1) % 0xffffff;
  return `${prefix}-${Date.now().toString(36)}-${seq.toString(36)}-${randomTail()}`;
}
